import { ContactMoldKind, CountryEnum, MoldConcentration } from '../../enums';
import { AsbestosFinding } from '../../sampleResults/asbestos';
import { DEFAULT_GERMAN_DATE_FORMAT } from '../../utils/dayjs';

export default {
  language: {
    'de': 'Deutsch',
    'en-US': 'English',
    'nl': 'Nederlands',
  },
  date: {
    format: DEFAULT_GERMAN_DATE_FORMAT,
  },
  common: {
    back: 'Terug',
    logout: 'Uitloggen',
    kcProfile: 'Gebruikersgegevens bewerken',
    downloadPdf: 'Downloaden',
    pageTitles: {
      callback: 'Aanmelden...',
      refresh: 'Vernieuwen...',
      activateSampleCode: 'Voorbeeldcode activeren',
      activateSample: 'Monster activeren',
      editSenderAddress: 'Verzendadres bewerken',
      addSampleComment: 'Opmerkingen aan monster toevoegen',
      generateSampleCodes: 'Voorbeeldcodes genereren',
      accountOverview: 'Accountoverzicht',
      resultDetails: 'Resultaatdetails',
    },
    nav: {
      overview: 'Overzicht',
      onlineshop: 'Webwinkel',
      onlineshopUrl: 'https://ivario.shop/',
      faq: 'Veelgestelde vragen & ondersteuning',
      faqUrl: 'https://ivario.shop/nl-nl/pages/help ',
      support: 'Steun',
      supportEmail: 'support@ivario.com',
      supportPhone: '+49 (0)40-180 24 111',
      supportPhoneInfo: '(Ma–Vr 09–17 uur)',
      showPhone: 'false',
      myAccount: 'Mijn account',
      myProfile: 'Mijn profiel',
      myOrders: 'Mijn bestellingen',
      myOrdersUrl: 'https://account.ivario.shop/',
      logOut: 'Uitloggen',
    },
  },
  errors: {
    emptyValue: 'Dit veld mag niet leeg zijn.',
    saveAddress:
      'Fout bij opslaan van adres of genereren van label. Controleer uw invoer nogmaals en probeer het opnieuw.',
  },
  component: {
    parameterDetail: {
      oneTab: 'Informatie',
      twoTab: 'Gezondheid',
      threeTab: 'Voeding',
      fourTab: 'Familie',
      noOneInfoAvailable:
        'Er is geen informatie beschikbaar voor deze parameter.',
      noTwoInfoAvailable:
        'Er is geen gezondheidsinformatie beschikbaar voor deze parameter.',
      noThreeInfoAvailable:
        'Er is geen voedingsinformatie beschikbaar voor deze parameter.',
      noFourInfoAvailable:
        'Er is geen familie-informatie beschikbaar voor deze parameter.',
    },
    sampleTeaser: {
      activationDate: 'Ingangsdatum',
      ACTIVATED: 'Geactiveerd',
      ARRIVED_IN_LAB: 'Aangekomen bij het laboratorium',
      SHIPPED: 'Verzonden door de klant',
      DONE: 'Resultaten beschikbaar',
    },
    shipmentInstructions: {
      shipmentLabelTitle: 'Verzendlabel voor laboratoriuminzending',
      downloadLabel: 'Verzendlabel downloaden',
      saveQrCode: 'QR-code opslaan',
      shipmentLabelHint:
        '→ Met beide knoppen wordt hetzelfde verzendlabel gedownload - één als pdf om af te drukken, één als QR-code voor je telefoon (bijvoorbeeld voor een pakketautomaat)',
      addAddress: 'Add address',
      foreignShippingInstructions:
        'Raadpleeg dit document voor informatie over hoe u uw monster naar ons laboratorium moet verzenden',
      foreignShippingInstructionsLink: 'Veelgestelde vragen.',
    },
    sampleDetails: {
      sampleId: 'Monster-ID',
      instructions: {
        image: 'Instructies voor het nemen van monsters',
        nextSteps: 'Volgende stappen',
        trackingLink: 'Zending volgen',
        step1: {
          title: 'Steekproeven',
          point1: 'Open de afnamekit',
          point2: 'Volg de bijgevoegde handleiding',
          point3: 'Neem het monster volgens de instructies',
        },
        step2: {
          title: 'Verzending',
          point1: 'Verpak het monster goed',
          point2: 'Bevestig het verzendlabel',
          point3: 'Breng het pakket naar het postkantoor',
          point4: 'Stuur het naar ons laboratorium',
        },
      },
      buttons: {
        downloadLabel: 'Verzendlabel downloaden',
        downloadCode: 'Voorbeeldcode downloaden',
      },
      status: {
        title: 'Status',
        ACTIVATED: 'Geactiveerd',
        ARRIVED_IN_LAB: 'Aangekomen bij het laboratorium',
        SHIPPED: 'Verzonden',
        DONE: 'Resultaten beschikbaar',
        undefined: 'geen status',
      },
      unknownField: '-',
      fields: {
        street: 'Straat',
        postalCode: 'Postcode',
        region: 'Regio',
        houseNumber: 'Huisnummer',
        createdAt: 'Gemaakt op',
        samplingPerson: 'Monsternemer',
        address: 'Adres',
        samplingLocation: 'Monsterlocatie',
        samplingDate: 'Datum van bemonstering',
        samplingTime: 'Bemonsteringstijd',
        companyName: 'Bedrijfsnaam',
        city: 'Stad',
        notes: 'Opmerkingen',
      },
      sampler: 'Monsternemer',
      edit: 'Bewerken',
    },
    parameterList: {
      remImageCaption:
        'De afbeelding van de röntgenmicroscoop laat zien of er al dan niet asbest of synthetische minerale vezels zijn aangetroffen. Asbestvezels zien er over het algemeen ruig uit en lijken, in lekentaal gezegd, op wolvezels of suikerspin. Synthetische minerale vezels lijken meer op draad: ze hebben een smalle kern zonder dat er veel vezels vanaf vertakken. Onderaan de afbeelding ziet u de schaalverdeling waarmee u de grootte kunt vergelijken met die van de vezel die is aangetroffen.',
      edxImageCaption:
        'Dit is een schema van energiedispersieve röntgenspectroscopie, kortweg EDX. Het materiaal dat u instuurt, wordt onderzocht op de aanwezige elementen die op verschillende energieniveaus (aangegeven in kW) reageren. Het resultaat wordt in het laboratorium vergeleken met de SEM-afbeelding om vast te stellen of het om asbestvezels, synthetische minerale vezels of onschadelijk materiaal gaat. De afzonderlijke velden in het schema komen overeen met de afkortingen van de elementen in het periodiek systeem.',
      moreInfoOnParam: 'Meer informatie over de parameter',
      errors: {
        noMoldDataAvailable: 'Er zijn geen gegevens over schimmels beschikbaar',
      },
      asbestosAnalysisError: {
        title: 'Fouten bij de asbestanalyse',
        description:
          'Neem voor meer informatie contact op met onze klantenservice.',
        backToProfile: 'Terug naar profiel',
      },
      moldAnalysisError: {
        title: 'Fouten tijdens de matrijsanalyse',
        description:
          'Neem voor meer informatie contact op met onze klantenservice.',
        backToProfile: 'Terug naar profiel',
      },
      asbestosDetection: {
        asbestosTitle: 'Asbest',
        kmfTitle: 'KMV',
        asbestosDetection: 'Asbestdetectie',
        kmfDetection: 'KMV-detectie',
        samplePreparation: 'Monstervoorbereiding',
        procedure: 'Procedure',
        proofThreshold: 'Drempel',
        finding: 'Bevinding',
      },
      moldValues: {
        [MoldConcentration.NO_MOLD]: 'Er is geen schimmel gedetecteerd',
        [MoldConcentration.LOW_MOLD]:
          'Er is een lage schimmelconcentratie gedetecteerd',
        [MoldConcentration.MEDIUM_MOLD]:
          'Er is een matige schimmelconcentratie vastgesteld',
        [MoldConcentration.HIGH_MOLD]:
          'Er is een hoge schimmelconcentratie vastgesteld',
        NA: 'Monster kan niet worden geanalyseerd of is uitgedroogd',
      },
      moldConcentration: {
        [MoldConcentration.NO_MOLD]: 'geen',
        [MoldConcentration.LOW_MOLD]: 'laag',
        [MoldConcentration.MEDIUM_MOLD]: 'matig',
        [MoldConcentration.HIGH_MOLD]: 'zwaar',
        [`${MoldConcentration.LOW_MOLD}_${ContactMoldKind.ANFLUG}`]:
          'geringe kolonisatie',
        [`${MoldConcentration.MEDIUM_MOLD}_${ContactMoldKind.ANFLUG}`]:
          'matige kolonisatie',
        [`${MoldConcentration.HIGH_MOLD}_${ContactMoldKind.ANFLUG}`]:
          'grootschalige kolonisatie',
        [`${MoldConcentration.LOW_MOLD}_${ContactMoldKind.BEFALL}`]:
          'geringe verontreiniging',
        [`${MoldConcentration.MEDIUM_MOLD}_${ContactMoldKind.BEFALL}`]:
          'matige verontreiniging',
        [`${MoldConcentration.HIGH_MOLD}_${ContactMoldKind.BEFALL}`]:
          'ernstige verontreiniging',
        [`${MoldConcentration.LOW_MOLD}_${ContactMoldKind.NONE}`]: 'laag',
        [`${MoldConcentration.MEDIUM_MOLD}_${ContactMoldKind.NONE}`]:
          'gematigd',
        [`${MoldConcentration.HIGH_MOLD}_${ContactMoldKind.NONE}`]: 'zwaag',
        name: 'blootstelling',
      },
      moldExposure: {
        name: 'Blootstelling',
      },
      keinBefund: 'Geen bevindingen',
      CFU: 'kolonievormende eenheden (KVE’s)',
      moldShortInfo: {
        [MoldConcentration.NO_MOLD]:
          'Er is geen schimmel aangetroffen in uw monster.',
        [MoldConcentration.LOW_MOLD]:
          'In uw monster is een lage concentratie schimmel aangetroffen.',
        [MoldConcentration.MEDIUM_MOLD]:
          'In uw monster is een matige concentratie schimmel aangetroffen.',
        [MoldConcentration.HIGH_MOLD]:
          'In uw monster is een hoge concentratie schimmel aangetroffen.',
      },
      asbestosValues: {
        [AsbestosFinding.CHRYSOTIL_DETECTED]: 'Chrysotielasbest aangetroffen',
        [AsbestosFinding.CHRYSOTIL_AMPHIBOLE_ANTHOPHYLLITE_DETECTED]:
          'Chrysotiel en amfiboolasbest (anthofylliet) aangetroffen',
        [AsbestosFinding.CHRYSOTIL_AMPHIBOLE_CROCIDOLITE_DETECTED]:
          'Chrysotiel en amfiboolasbest (krokydoliet) aangetroffen',
        [AsbestosFinding.CHRYSOTIL_AMPHIBOLE_AMOSITE_DETECTED]:
          'Chrysotiel en amfiboolasbest (amosiet) aangetroffen',
        [AsbestosFinding.CHRYSOTIL_AMPHIBOLE_TREMOLITE_DETECTED]:
          'Chrysotiel en amfiboolasbest (tremoliet) aangetroffen',
        [AsbestosFinding.CHRYSOTIL_AMPHIBOLE_ACTINOLITE_DETECTED]:
          'Chrysotiel en amfiboolasbest (actinoliet) aangetroffen',
        [AsbestosFinding.NO_ASBESTOS_DETECTED]:
          'Er is geen asbest aangetroffen',
        [AsbestosFinding.NO_ASBESTOS_INORGANIC_FIBER]:
          'Geen asbest aangetroffen (andere anorganische vezels)',
        [AsbestosFinding.NO_ASBESTOS_ORGANIC_FIBER]:
          'Geen asbest aangetroffen (organische vezels)',
        [AsbestosFinding.NO_ASBESTOS_ORGANIC_FIBER_ALT]:
          'Geen asbest aangetroffen (andere vezels aangetroffen)',
        [AsbestosFinding.NO_ASBESTOS_TALK_DETECTED]:
          'Geen asbest aangetroffen (talk aangetroffen)',
        [AsbestosFinding.AMPHIBOLE_TREMOLITE_DETECTED]:
          'Amfiboolasbest (tremoliet) aangetroffen',
        [AsbestosFinding.AMPHIBOLE_AMOSITE_DETECTED]:
          'Amfiboolasbest (amosiet) aangetroffen',
        [AsbestosFinding.AMPHIBOLE_ANTHOPHYLLITE_DETECTED]:
          'Amfiboolasbest (anthofylliet) aangetroffen',
        [AsbestosFinding.AMPHIBOLE_CROCIDOLITE_DETECTED]:
          'Amfiboolasbest (krokydoliet) aangetroffen',
        [AsbestosFinding.AMPHIBOLE_ACTINOLITE_DETECTED]:
          'Amfiboolasbest (actinoliet) aangetroffen',
        [AsbestosFinding.TRACE]: 'Sporen',
        [AsbestosFinding.PERCENT_1_TO_5]: '1-5%',
        [AsbestosFinding.PERCENT_5_TO_20]: '5-20%',
        [AsbestosFinding.PERCENT_20_TO_50]: '20-50%',
        [AsbestosFinding.PERCENT_OVER_50]: '> 50%',
        [AsbestosFinding.NOT_POSSIBLE]: '',
        [AsbestosFinding.MINUS]: '',
        [AsbestosFinding.AMF_NOT_DETECTED]:
          'Er zijn geen kunstmatige minerale vezels aangetroffen',
        [AsbestosFinding.AMF_DETECTED_NON_WHO]:
          'Er zijn kunstmatige minerale vezels aangetroffen (vezels die niet door de WHO zijn erkend)',
        [AsbestosFinding.AMF_DETECTED_WHO]:
          'Er zijn kunstmatige minerale vezels aangetroffen (WHO-vezels)',
      },
      notAnalyzable: 'NA',
      notAnalyzableLong: 'Niet analyseerbaar',
      notDetectable: 'ND',
      notDetectableLong: 'Niet detecteerbaar',
      exceededRegulation: 'Limiet overschreden',
      closeToExceedingRegulation: 'Bijna aan de limiet',
      parameter: 'Parameter',
      value: 'Waarde',
      evaluation: 'Evaluatie',
      status: 'Status',
      details: 'Details weergeven',
      recommendations: 'Aanbevelingen',
      statusTypes: {
        error: 'Problematisch',
        warning: 'Op het randje',
        success: 'Normaal',
        unset: 'Niet ingesteld',
        notAnalyzable: 'Niet analyseerbaar',
      },
      sort: {
        label: 'Sorteren op',
        CATEGORY_ASC: 'Parametergroep (A-Z)',
        CATEGORY_DESC: 'Parametergroep (Z-A)',
        ALPHABET_ASC: 'Alfabetisch (A-Z)',
        ALPHABET_DESC: 'Alfabetisch (Z-A)',
        DEVIATION_ASC: 'Afwijking van de grenswaarde (stijgend)',
        DEVIATION_DESC: 'Afwijking van de grenswaarde (dalend)',
      },
    },
    impersonationBanner: {
      message:
        'U bevindt zich op een scherm van de klantenservice dat alleen-lezen is; bepaalde functies, zoals het bewerken van voorbeeldgegevens, werken hier niet',
    },
    announcementBar: {
      readMore: 'Meer lezen',
      readLess: 'Minder lezen',
    },
  },
  view: {
    commentsForm: {
      title: 'Voeg opmerkingen toe aan je voorbeeld',
      description: 'Deze gegevens zullen in uw analyseresultaten te zien zijn.',
      samplingPoint: 'Bemonsteringslocatie',
      companyName: 'Bedrijfsnaam',
      street: 'Straat',
      houseNumber: 'Huisnummer',
      postalCode: 'Postcode',
      city: 'Stad',
      samplingDate: 'Datum van bemonstering',
      samplingTime: 'Bemonsteringstijd',
      sampler: 'Monsternemer',
      notes: 'Opmerkingen',
      saveButton: 'Reacties opslaan',
      back: '$t(common:back)',
    },
    error: {
      apology: 'Sorry, er is een fout opgetreden.',
      details: '{{stack}}',
      buttons: {
        backToProfile: 'Terug naar het profieloverzicht',
        logout: '$t(common:logout)',
      },
      empty: 'Het veld mag niet leeg zijn.',
      connectionError: 'Verbindingsfout',
      503: 'Dienst niet beschikbaar',
    },
    profile: {
      welcome: 'Welkom {{name}}!',
      addSample: 'Voorbeeldcode toevoegen',
      title: 'Alle monsters',
      result: 'Resultaat',
      details: 'Details',
      latestSample: {
        nextStep: 'Volgende stappen voor het monster',
        title: 'Stuur nu een monster: {{productName}}',
        infoText:
          'Uw monster is succesvol geactiveerd. Neem vervolgens een monster zoals beschreven in de bijsluiter en stuur dit naar ons laboratorium.',
      },
    },
    registerSample: {
      title: 'Voorbeeldcode activeren',
      subtitle:
        'Voer uw voorbeeldcode in om uw analyse te activeren en door te gaan met het indienen.',
      hintTitle: 'Waar kan ik mijn voorbeeldcode vinden?',
      hintDescription:
        'Dit staat op de eerste pagina van de bijgevoegde handleiding.',
      code: 'Voorbeeldcode',
      codePlaceholder: 'bijv. sabu3-xuwe2-vafu2',
      inputNote: 'Hoofdlettergevoelig',
      verify: 'Doorgaan',
      invalidCode: 'Ongeldige voorbeeldcode',
      cancelledCode:
        'Deze voorbeeldcode kan niet worden geactiveerd. Neem contact op met de support als u vragen heeft.',
      empty: '$t(view:error.empty)',
    },
    landing: {
      tabTitle: 'IVARIO-login',
      metaDescription:
        'Bekijk de laboratoriumresultaten van uw wateranalyse of asbestonderzoek veilig en snel online. Log nu in op uw IVARIO-klantaccount!',
      pageTitle: 'Activeer uw proefexemplaar',
      pageSub: 'Registreer u of log in om uw monster in te dienen.',
      newUser: {
        title: 'Ik ben nieuw hier',
        description:
          'Heb je nog geen account? Ook als je al eerder bij ons hebt besteld, moet je je registreren in het Analyses Report Center.',
        nextStepsLabel: 'Wat gebeurt er nu:',
        steps: [
          'Bevestig je e-mailadres',
          'Voer voorbeeldcode in',
          'Verzendlabel aanmaken',
          'Resultaten ontvangen',
        ],
        cta: 'Schrijf je nu in',
      },
      existingUser: {
        title: 'Ik heb al een account',
        description:
          'Ik heb al een account in het IVARIO Analyses Report Center.',
        info: [
          'Voorbeelden bekijken en beheren',
          'Analyseverslag downloaden',
          'Verzendlabel aanmaken',
        ],
        cta: 'Inloggen',
      },
      oldResults:
        'Bent u op zoek naar analyseresultaten van monsters die vóór oktober 2025 zijn genomen?',
      helpPrefix: 'Vragen?',
      helpLink: 'Neem contact op met de klantenservice',
      supportUrl: 'https://support.ivario.eu/en/support/home',
      headerLangLabel: 'Taal:',
      overlay: {
        title: 'Taal gedetecteerd',
        sub: 'We hebben vastgesteld dat uw webbrowser op het <strong>Nederlands</strong> is ingesteld.<br>Wilt u verdergaan in het Nederlands?',
        confirm: 'Wilt u verdergaan in het Nederlands?',
        or: 'of kies een andere taal',
        otherLabel: 'Andere taal:',
        selectBtn: 'Ga verder in de geselecteerde taal',
      },
    },
    senderAddress: {
      title: 'Nieuw voorbeeld',
      success: 'Registratie geslaagd',
      failure: 'De registratie is mislukt',
      save: 'Opslaan',
    },
    result: {
      title: 'Analyse resultaat: {{id}}',
      loading: 'Resultaat wordt geladen...',
    },
    internal: {
      generateSampleCodes: {
        title: 'Voorbeeldcodes genereren',
        selectProduct: 'Kies een product',
        numberOfCodes: 'Hoeveel voorbeeldcodes wilt u genereren?',
        generatedFor: 'Opgemaakt voor',
        generatedForPlaceholder: 'bijv. Obi, partij 1002, beurs Hannover',
        generate: 'Voorbeeldcodes genereren',
        close: 'Sluiten',
        error: 'Fout bij het genereren van voorbeeldcodes',
        minimumCodes: 'Het aantal codes moet ten minste één zijn.',
        generatedForRequired:
          'Geef ten minste vijf tekens in voor het doel waarvoor de codes worden gegenereerd.',
      },
    },
    footer: {
      shipping: 'Verzending en betaling',
      dataPrivacy: 'Gegevensbescherming',
      blog: 'IVARIO blog',
      experience: 'Klantbeoordelingen',
      testResult: 'Naar het oude systeem vóór oktober 2025',
      agb: 'Algemene voorwaarden en herroepingsrecht',
      faq: 'Veelgestelde vragen en contact',
      waterWiki: 'Blog over drinkwater',
      imprint: 'Colofon',
      testCompare: 'Vergelijking van watertests',
      moldWiki: 'Blog over schimmel',
      contact: 'Contact',
      accreditation: 'Accreditatie',
      affiliate: 'Affiliatepartners',
      asbestos: 'Asbestos consultant',
      agbUrl: 'https://ivario.com/nl-nl/policies/terms-of-service',
      dataPrivacyUrl: 'https://ivario.com/nl-nl/policies/privacy-policy',
    },
    senderAddressForm: {
      title: 'Afzenderadres toevoegen',
      subtitle:
        'Om een verzendlabel aan te maken, moet eerst een afzenderadres worden opgegeven. Gratis verzendlabels kunnen alleen worden aangemaakt voor Duitsland, Oostenrijk en Nederland.',
      lastName: 'Achternaam',
      firstName: 'Voornaam',
      street: 'Straat',
      houseNumber: 'Huisnummer',
      postalCode: 'Postcode',
      city: 'Stad',
      country: 'Land',
      companyName: 'Bedrijfsnaam (optioneel)',
      saveSenderAddress: 'Afzenderadres opslaan',
    },
  },
  countries: {
    [CountryEnum.Germany]: 'Duitsland',
    [CountryEnum.Austria]: 'Oostenrijk',
    [CountryEnum.Belgium]: 'België',
    [CountryEnum.Bulgaria]: 'Bulgarije',
    [CountryEnum.Croatia]: 'Kroatië',
    [CountryEnum.Cyprus]: 'Cyprus',
    [CountryEnum['Czech Republic']]: 'Tsjechië',
    [CountryEnum.Denmark]: 'Denemarken',
    [CountryEnum.Estonia]: 'Estland',
    [CountryEnum.Finland]: 'Finland',
    [CountryEnum.France]: 'Frankrijk',
    [CountryEnum.Greece]: 'Griekenland',
    [CountryEnum.Hungary]: 'Hongarije',
    [CountryEnum.Ireland]: 'Ierland',
    [CountryEnum.Italy]: 'Italië',
    [CountryEnum.Latvia]: 'Letland',
    [CountryEnum.Lithuania]: 'Litouwen',
    [CountryEnum.Luxembourg]: 'Luxemburg',
    [CountryEnum.Malta]: 'Malta',
    [CountryEnum.Netherlands]: 'Nederland',
    [CountryEnum.Poland]: 'Polen',
    [CountryEnum.Portugal]: 'Portugal',
    [CountryEnum.Romania]: 'Roemenië',
    [CountryEnum.Slovakia]: 'Slowakije',
    [CountryEnum.Slovenia]: 'Slovenië',
    [CountryEnum.Spain]: 'Spanje',
    [CountryEnum.Sweden]: 'Zweden',
    [CountryEnum.Switzerland]: 'Zwitserland',
  },
  toasts: {
    sampleClaimed: {
      title: 'Succesvol geactiveerd',
      subTitle: 'Uw voorbeeld is succesvol geactiveerd',
    },
    sampleClaimedFailed: {
      title: 'Fout',
      subTitle: 'Uw voorbeeld kon niet worden geactiveerd',
    },
  },
  legacy: {
    register: 'Heb je het voorbeeld al geactiveerd in het oude systeem? ',
    registerLink: 'Log hier in\u00A0→',
    sampleList:
      'Als u een oud account bij IVARIO hebt (aangemaakt vóór oktober 2025), kunt u ',
    sampleListLink: 'hier',
    sampleListOutro:
      ' ons oude systeem vinden om uw geregistreerde monsters en analyserapporten te bekijken.',
  },
} as const;
